import { z } from 'zod';
import { EXAMS } from './exam';
import { DifficultySchema, ImageQuestionFieldsSchema, QuestionStatusSchema } from './question';
import { VoteTypeSchema } from './voting';

// Schemas are authoritative; TS types are derived via z.infer<>. `specialty`
// axes use z.string() (SpecialtyName is structurally `string`, and the repo
// deliberately doesn't hard-enum specialty to avoid 400ing legacy values).

/**
 * Question types accepted across the app. Aligned with question.ts's
 * `QuestionTypeEnum` (4 values). 'short_answer' was REMOVED (P1.3): it had zero
 * producers across the generator, backend, and DB.
 */
export const QuestionTypeSchema = z.enum(['multiple_choice', 'true_false', 'sorting', 'flashcard']);
export type QuestionType = z.infer<typeof QuestionTypeSchema>;

/**
 * Quiz session mode: 'tutor' gives immediate per-question feedback, 'timed' simulates exam conditions.
 */
export const QuizModeSchema = z.enum(['tutor', 'timed']);
export type QuizMode = z.infer<typeof QuizModeSchema>;

// Quiz + question read/view shapes. QuestionViewData layers the view fields on
// the shared ImageQuestionFields mix-in.
export const QuestionViewDataSchema = ImageQuestionFieldsSchema.extend({
  id: z.string(),
  text: z.string(),
  explanation: z.string(),
  questionType: QuestionTypeSchema.optional(),
  // Loose/open `content` + `answer`: consumers read fields directly without
  // narrowing on questionType. The authoritative discriminated forms are
  // QuestionContent / QuestionAnswer in question.ts; tightening these to the
  // discriminated union is deferred to the client-cleanup phase (P1.3 Phase 2).
  content: z
    .object({
      options: z.array(z.string()).optional(),
      items: z.array(z.object({ id: z.string(), text: z.string() })).optional(),
      labels: z.object({ true: z.string(), false: z.string() }).optional(),
      // Flashcard "back" — projected from answer.text on public sanitize — and whether the
      // server can grade a typed answer for it.
      backText: z.string().optional(),
      guidedGrading: z.boolean().optional(),
    })
    .catchall(z.unknown())
    .optional(),
  answer: z
    .object({
      correctOptionIndex: z.number().optional(),
      correctOrder: z.array(z.string()).optional(),
      correct: z.boolean().optional(),
      text: z.string().optional(),
    })
    .catchall(z.unknown())
    .optional(),
  // Taxonomy fields (three-axis contract; mirrors Question).
  specialty: z.string(),
  subSpecialty: z.string().nullable().optional(),
  topics: z.array(z.string()),
  forExams: z.array(z.enum(EXAMS)),
  difficulty: DifficultySchema,
  status: QuestionStatusSchema.optional(),
  /**
   * Set by the server when moderation has withdrawn this question. The substance — text, image,
   * options, answer, explanation — is stripped before the payload leaves the backend, and this
   * flag is what remains so a client can render a placeholder in its place.
   *
   * The entry is kept rather than dropped ON PURPOSE: a quiz attempt's answers are paired to the
   * quiz's STORED question refs by position and validated against their count, so removing one
   * would shift every later answer and make the quiz unsubmittable.
   *
   * Deliberately a decision, not a state: `status` is the moderation state machine and the policy
   * for reading it lives in one place on the server (apps/backend/src/utils/questionVisibility).
   * Clients render this flag instead of re-implementing that policy.
   */
  withdrawn: z.boolean().optional(),
  createdAt: z.string().optional(), // ISO string
  updatedAt: z.string().optional(), // ISO string
  upvotes: z.number().optional(),
  downvotes: z.number().optional(),
  voteCount: z.number().optional(),
  score: z.number().optional(),
  userVote: VoteTypeSchema.optional(),
  isBookmarked: z.boolean().optional(),
  // Expanded explanation fields
  references: z.array(z.string()).optional(),
  pitfalls: z.array(z.string()).optional(),
  clinicalPearls: z.array(z.string()).optional(),
  backToBasics: z.array(z.string()).optional(),
});
export type QuestionViewData = z.infer<typeof QuestionViewDataSchema>;

export const QuizQuestionRefSchema = z.object({
  order: z.number(),
  questionId: z.string(),
});
export type QuizQuestionRef = z.infer<typeof QuizQuestionRefSchema>;

export const QuizAdSchema = z.object({
  id: z.string(),
  imageUrl: z.string().optional(),
  text: z.string().optional(),
  ctaText: z.string().optional(),
  targetUrl: z.string().optional(),
  type: z.string().optional(), // e.g., "quiz_interstitial"
  directBuy: z.boolean().optional(),
});
export type QuizAd = z.infer<typeof QuizAdSchema>;

export const QuizViewDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(), // Frontend field
  subject: z.string(),
  difficulty: DifficultySchema.optional(),
  message: z.string().optional(),
  createdBy: z.string(),
  createdAt: z.string(), // ISO string
  updatedAt: z.string().optional(), // ISO string
  questions: z.array(QuizQuestionRefSchema),
  populatedQuestions: z.array(QuestionViewDataSchema).optional(),
  attempts_count: z.number(),
  latest_attempt: z
    .object({
      id: z.string(),
      score: z.number(),
      submitted_at: z.string(), // ISO String - standardized to snake_case
    })
    .nullable()
    .optional(),
  mode: QuizModeSchema.optional(), // tutor (immediate feedback) or timed (exam sim)
  secondsPerQuestion: z.number().optional(),
  is_public: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  adPlacement: z
    .object({
      frequency: z.number(), // Show ad every N questions
      enabled: z.boolean(),
      ads: z.array(QuizAdSchema).optional(),
    })
    .optional(),
});

/**
 * `Quiz` is the public name for a quiz as returned by the API — it is what
 * consumers import (e.g. apps/mobile/services/api.ts). `QuizViewDataSchema`
 * above is the underlying shape.
 */
export const QuizSchema = QuizViewDataSchema;
export type Quiz = z.infer<typeof QuizSchema>;

export const AnsweredQuestionDataSchema = z.object({
  questionId: z.string(),
  userAnswer: z.any().optional(), // Polymorphic answer
  correctAnswer: z.any().optional(), // Polymorphic correct answer
  isCorrect: z.boolean(),
  question: QuestionViewDataSchema.optional(), // Full question data for rich attempt records
  questionText: z.string().optional(), // Mobile field
  explanation: z.string().optional(), // Mobile field
  /**
   * Same meaning as `QuestionViewData.withdrawn`, on the post-submit review payload. Without it the
   * results screen would re-display the text and correct answer of a question the quiz screen had
   * just replaced with a placeholder. The answer is still graded and still counted — only its
   * display fields are blanked.
   */
  withdrawn: z.boolean().optional(),
  // Persisted on each answered question in a quiz attempt and read back by
  // ProgressModel to bucket per-subject metrics. The NAME is legacy — the value
  // is the canonical `question.specialty`, not the dead `question.subject`
  // generation relic. Optional because the backend runs Firestore with
  // `ignoreUndefinedProperties: true` (apps/backend/src/config/database.ts), so
  // an undefined specialty drops the key rather than writing it.
  subject: z.string().optional(),
});
export type AnsweredQuestionData = z.infer<typeof AnsweredQuestionDataSchema>;

export const QuizAttemptViewDataSchema = z.object({
  id: z.string(),
  quizId: z.string(),
  userId: z.string(),
  answeredQuestions: z.array(AnsweredQuestionDataSchema),
  overallScore: z.number(),
  started_at: z.string(), // ISO String - standardized to snake_case
  completed_at: z.string(), // ISO String - standardized to snake_case
  timeSpentSeconds: z.number(),
  quizTitle: z.string().optional(), // Populated field
  quizSubject: z.string().optional(), // Populated field
});
export type QuizAttemptViewData = z.infer<typeof QuizAttemptViewDataSchema>;

/** Alias for `QuizAttemptViewData` — preferred public name across mobile. */
export const QuizAttemptSchema = QuizAttemptViewDataSchema;
export type QuizAttempt = z.infer<typeof QuizAttemptSchema>;

/**
 * Response of `POST /quizzes/:id/attempts` — the immediate submit result. Distinct
 * from the stored `QuizAttemptViewData`: this carries `score` (not `overallScore`),
 * the freshly-created attemptId, and the answered-question breakdown.
 */
export const QuizAttemptResultSchema = z.object({
  attemptId: z.string(),
  score: z.number(),
  answeredQuestions: z.array(AnsweredQuestionDataSchema),
});
export type QuizAttemptResult = z.infer<typeof QuizAttemptResultSchema>;

// Result of verifying an answer for a question
export const QuestionVerificationResultSchema = z.object({
  isCorrect: z.boolean(),
  correctAnswerIndex: z.number().nullable().optional(),
  explanation: z.string().nullable().optional(),
  answerCounts: z.array(z.number()).optional(),
  answerPercentages: z.array(z.number()).optional(),
  totalResponses: z.number().nullable().optional(),
  correctOrder: z.array(z.string()).optional(),
  // Flashcard-specific self-assessment results
  flashcardStats: z
    .object({
      correctCount: z.number(),
      incorrectCount: z.number(),
      correctPercentage: z.number(),
    })
    .optional(),
});
export type QuestionVerificationResult = z.infer<typeof QuestionVerificationResultSchema>;

export const ReportReasonSchema = z.enum([
  'incorrect_answer',
  'outdated_information',
  'unclear_question',
  'inappropriate_content',
  'duplicate',
  'other',
]);
export type ReportReason = z.infer<typeof ReportReasonSchema>;

/**
 * The lifecycle of every user report, question or comment: pending until an admin acts, then
 * dismissed (the report is rejected; a hidden comment is restored) or actioned (the content is
 * removed: the question withdrawn, the comment deleted).
 */
export const ReportStatusSchema = z.enum(['pending', 'dismissed', 'actioned']);
export type ReportStatus = z.infer<typeof ReportStatusSchema>;

export const QuestionReportSchema = z.object({
  id: z.string(),
  questionId: z.string(),
  reporterId: z.string(),
  reason: ReportReasonSchema,
  description: z.string(),
  status: ReportStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  // Populated fields for admin display
  questionText: z.string().optional(),
  reporterEmail: z.string().optional(),
  reporterDisplayName: z.string().optional(),
  resolvedBy: z.string().optional(),
  resolvedAt: z.string().optional(),
});
export type QuestionReport = z.infer<typeof QuestionReportSchema>;

// Question filtering and management
export const QuestionFilterParamsSchema = z.object({
  specialty: z.string().optional(), // matches Question.specialty
  difficulty: z.array(DifficultySchema).optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
  incorrectOnly: z.boolean().optional(),
  newToUser: z.boolean().optional(),
});
export type QuestionFilterParams = z.infer<typeof QuestionFilterParamsSchema>;
